<template>
  <div>
    <H1 font-family="Bitter" color="#ffffff" center>
      {{ $t(`configurator.heading.${mode}`) }}
    </H1>
    <div class="container bg-white">
      <breadcrumbs :items="steps" :current-item="currentStep" @item-click="navigate" />
      <spinner
        v-if="isLoading"
        :color="configuratorData.PrimaryColor ? configuratorData.PrimaryColor : null"
        class="m-b-xxl m-t-xxl align-center"
      />
      <div v-if="!isLoading">
        <div v-if="currentStep === 'tourSelection'">
          <product-selection :products="products" @product-selected="onProductSelected" />
        </div>
        <div v-if="currentStep === 'extraSelection'">
          <extra-selection
            :extras="extras"
            :orderTitle="orderData.product.Title"
            :selected-extras="order.extras"
            @extras-selected="onExtrasSelected"
          />
        </div>
        <div v-if="currentStep === 'upsellSelection'">
          <upsell-selection
            :upsells="upsells"
            :orderTitle="orderData.product.Title"
            :selected-upsells="order.upsells"
            :min-participants="orderData.product.MinParticipants"
            :max-participants="orderData.product.MaxParticipants"
            :peopleCount="orderData.details.peopleCount"
            :playDate="orderData.details.playDate"
            :playTime="orderData.details.playTime"
            :product="orderData.product"
            @form-data-filled="onGroupDataSelected"
            @upsells-selected="onUpsellsSelected"
          />
        </div>
        <div v-if="currentStep === 'checkout'">
          <customer-form
            :form-fields="customerPersonalDataForm"
            :order="orderData"
            :price-total="priceTotal"
            :price-per-person="pricePerPerson"
            :testimonials="testimonials"
            :mode="mode"
            @checkout="onCheckout"
            @form-data-filled="onRequest"
            @customer-form-interaction="onCustomerFormInteraction"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import spinner from '@/components/common/spinner';
import breadcrumbs from '@/components/common/breadcrumbs/breadcrumbs.vue';
import { H1 } from '@/components/common/styledHeading';
import productSelection from '@/components/configuratorParts/productSelection.vue';
import extraSelection from '@/components/configuratorParts/extraSelection.vue';
import upsellSelection from '@/components/configuratorParts/upsellSelection.vue';
import customerForm from '@/components/configuratorParts/customerForm.vue';
import customerPersonalDataForm from '@/config/customerPersonalDataForm';

export default {
  name: 'configurator',

  components: {
    spinner,
    breadcrumbs,
    H1,
    productSelection,
    extraSelection,
    upsellSelection,
    customerForm,
  },

  watch: {
    async selectedProductId() {
      this.isLoading = true;
      await Promise.all([
        this.fetchExtras({ productId: this.selectedProductId }),
        this.fetchUpsells({ productId: this.selectedProductId }),
        this.fetchTestimonials(),
      ]);
      this.isLoading = false;
    },
  },

  data() {
    return {
      isLoading: false,
      mode: '',
      steps: [
        { id: 'tourSelection', name: this.$t('configurator.chooseTour') },
        { id: 'extraSelection', name: this.$t('configurator.extras') },
        { id: 'upsellSelection', name: this.$t('configurator.specials') },
        { id: 'checkout', name: this.$t('configurator.checkoutEmojis') },
      ],
      currentStep: '',
      selectedProductId: '',
      customerPersonalDataForm,
    };
  },

  computed: {
    ...mapGetters({
      configuratorData: 'configurators/getConfiguratorData',
      products: 'configurators/getProducts',
      extras: 'configurators/getExtras',
      upsells: 'configurators/getUpsells',
      testimonials: 'configurators/getTestimonials',
      order: 'configurators/getOrder',
    }),

    orderData() {
      const { details } = this.order;
      const product = this.products.find((e) => e.id === this.order.product);
      const orderExtras = this.order.extras.map((e) => this.extras.find((extra) => extra.id === e));
      const orderUpsells = this.order.upsells.map((e) => this.upsells.find((upsell) => upsell.id === e));
      return { details, product, extras: orderExtras, upsells: orderUpsells };
    },

    priceTotal() {
      const allItems = [this.orderData.product, ...this.orderData.extras, ...this.orderData.upsells];
      return this.calcPriceTotal(allItems, this.orderData.details.peopleCount);
    },

    pricePerPerson() {
      return this.priceTotal / this.orderData.details.peopleCount;
    },

    /** Builds the ecommerce items array used by multiple tracking events. */
    ecommerceItems() {
      return [
        {
          item_id: this.orderData.product.id,
          item_name: this.orderData.product.Title,
          price: this.orderData.product.Price,
          quantity: this.orderData.details.peopleCount,
        },
        ...this.orderData.extras.map((extra) => ({
          item_id: extra.id,
          item_name: extra.Title,
          price: extra.Price,
          quantity: this.orderData.details.peopleCount,
        })),
        ...this.orderData.upsells.map((upsell) => ({
          item_id: upsell.id,
          item_name: upsell.Title,
          price: upsell.Price,
          quantity: this.orderData.details.peopleCount,
        })),
      ];
    },
  },

  beforeMount() {
    const { productId } = this.$route.query;
    if (productId) {
      this.onProductSelected(productId);
      return;
    }
    this.currentStep = 'tourSelection';
  },

  async mounted() {
    this.isLoading = true;
    this.setBookingMode(this.$route.query.mode);
    await this.fetchProducts({ configuratorId: this.configuratorData.id });
    this.isLoading = false;
  },

  methods: {
    ...mapActions('configurators', [
      'fetchProducts',
      'fetchExtras',
      'fetchUpsells',
      'fetchTestimonials',
      'setSelectedProduct',
      'setSelectedExtras',
      'setSelectedUpsells',
      'setOrderDetails',
      'removeSelectedExtras',
      'removeSelectedUpsells',
      'addToSnipcart',
      'buildSnipcartProductDefinition',
      'submitRequest',
    ]),

    // -----------------------------------------------------------------------
    // Tracking
    // -----------------------------------------------------------------------

    trackEvent(event, extra = {}) {
      window.parent.postMessage({ event, ...extra });
    },

    trackEcommerceEvent(event, extra = {}) {
      this.trackEvent(event, {
        ecommerce: {
          currency: 'EUR',
          value: this.priceTotal,
          items: this.ecommerceItems,
        },
        ...extra,
      });
    },

    // -----------------------------------------------------------------------
    // Navigation
    // -----------------------------------------------------------------------

    navigate(to) {
      if (this.currentStep === to) return;
      if (this.currentStep === 'tourSelection') return;
      if (to === 'checkout') return;
      if (this.currentStep === 'extraSelection' && to === 'tourSelection') {
        this.removeSelectedExtras();
      }
      if (this.currentStep === 'upsellSelection' && to === 'extraSelection') {
        this.removeSelectedUpsells();
      }
      if (this.currentStep === 'checkout' && to === 'upsellSelection') {
        this.removeSelectedUpsells();
      }
      this.currentStep = to;
    },

    setBookingMode(mode) {
      switch (mode) {
        case 'booking':
          this.mode = 'booking';
          break;
        case 'inquiry':
          this.mode = 'inquiry';
          break;
        default:
          this.mode = 'configurator';
      }
    },

    // -----------------------------------------------------------------------
    // Step handlers
    // -----------------------------------------------------------------------

    onProductSelected(productId) {
      this.selectedProductId = productId;
      this.setSelectedProduct({ productId });
      this.trackEvent('product.selected', {
        ecommerce: {
          items: [{ item_name: this.orderData.product?.Title }],
        },
      });
      this.currentStep = 'extraSelection';
    },

    onExtrasSelected(selectedExtras) {
      this.setSelectedExtras({ selectedExtras });
      this.trackEvent('extras.selected', {
        ecommerce: {
          items: this.orderData.extras?.map((extra) => ({
            item_id: extra.id,
            item_name: extra?.Title,
            price: extra.Price,
            quantity: this.orderData.details.peopleCount,
          })),
        },
      });
      this.currentStep = 'upsellSelection';
    },

    onGroupDataSelected(formData) {
      this.setOrderDetails({ formData });
    },

    onUpsellsSelected(selectedUpsells) {
      this.isLoading = true;
      this.setSelectedUpsells({ selectedUpsells });
      this.trackEvent('upsells.selected');
      this.trackEcommerceEvent('add_to_cart');
      this.currentStep = 'checkout';
      this.isLoading = false;
    },

    onCustomerFormInteraction() {
      this.trackEcommerceEvent('add_payment_info');
    },

    // -----------------------------------------------------------------------
    // Checkout — Snipcart booking
    // -----------------------------------------------------------------------

    buildCartDescription() {
      const parts = [`${this.orderData.details.peopleCount} ${this.$t('forms.persons')}`];
      if (this.orderData.extras.length > 0) {
        parts.push(`${this.$t('configurator.extras')}: ${this.orderData.extras.map((e) => e.Title).join(', ')}`);
      }
      if (this.orderData.upsells.length > 0) {
        parts.push(`${this.$t('configurator.specials')}: ${this.orderData.upsells.map((u) => u.Title).join(', ')}`);
      }
      return parts.join(' | ');
    },

    /**
     * Returns true when the configurator is embedded inside an iframe.
     */
    isInIframe() {
      try {
        return window.self !== window.top;
      } catch {
        // Cross-origin restriction – we are definitely in an iframe
        return true;
      }
    },

    /**
     * Navigates the top-level window to the iframe's URL, stripping the
     * `layoutMode` query-param (so the page renders standalone) and adding
     * an `openCart` flag so it immediately opens the Snipcart cart after
     * loading the pending product from localStorage.
     */
    redirectToCheckout(productDefinition) {
      localStorage.setItem('pendingSnipcartCheckout', JSON.stringify(productDefinition));

      const url = new URL(window.location.href);
      url.searchParams.delete('layoutMode');
      url.searchParams.set('openCart', 'true');
      window.top.location.href = url.toString();
    },

    async onCheckout() {
      this.isLoading = true;
			this.trackEcommerceEvent('checkout', { details: this.order.details });
      try {
        const checkoutPayload = {
          configuratorId: this.configuratorData.id,
          orderValue: this.priceTotal,
          description: this.buildCartDescription(),
        };

        if (this.isInIframe()) {
          // PayPal Express Checkout does not work inside iframes.
          // Build the product definition, stash it in localStorage, and
          // open the configurator in a standalone tab where PayPal works.
          const productDefinition = await this.buildSnipcartProductDefinition(checkoutPayload);
          this.redirectToCheckout(productDefinition);
        } else {
          await this.addToSnipcart(checkoutPayload);
        }

      } catch (error) {
        console.error('Checkout failed:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // -----------------------------------------------------------------------
    // Request — Inquiry form submission
    // -----------------------------------------------------------------------

    async onRequest(formData) {
      this.isLoading = true;
      try {
        this.setOrderDetails({ formData });
        await this.submitRequest({
          configuratorId: this.configuratorData.id,
          orderValue: this.priceTotal,
        });
        this.trackEcommerceEvent('checkout', { details: this.order.details });
        this.$router.push({ name: 'thank-you' });
      } catch (error) {
        console.error('Request submission failed:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    calcPriceTotal(orderItems, peopleCount) {
      return orderItems.reduce(
        (total, { PriceType, Price }) =>
          total + (PriceType !== 'Flat' ? Price * peopleCount : Price),
        0,
      );
    },
  },
};
</script>
